import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowDown,
  ArrowRight,
  BrainCircuit,
  Check,
  Database,
  Eye,
  Gauge,
  Layers,
  ShieldAlert,
  Sparkles,
  Target,
  Workflow,
} from "lucide-react";
import { useEmpresasLanguage } from "../context/EmpresasLanguageContext";
import {
  FI_HEALTHCARE_WHATSAPP,
  financialIntelligenceHealthcare,
} from "../data/financialIntelligenceHealthcare";
import sectorSalud from "../assets/sector-salud.jpg";

const CAPABILITY_ICONS = [Eye, Sparkles, ShieldAlert, Workflow, Layers];
const DECISION_ICONS = [Database, Activity, BrainCircuit, Layers, Target];

function DecisionVisual({ copy }) {
  return (
    <div className="relative rounded-2xl border border-white/15 bg-white/10 p-5 sm:p-6 backdrop-blur-md shadow-[0_24px_60px_-28px_rgba(0,0,0,0.55)]">
      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-emerald-300">
        {copy.title}
      </p>

      <ol className="mt-5 grid grid-cols-5 gap-1.5 sm:gap-2">
        {copy.steps.map((step, index) => {
          const Icon = DECISION_ICONS[index] || Target;
          const isLast = index === copy.steps.length - 1;
          return (
            <li key={step} className="relative flex flex-col items-center text-center">
              {index < copy.steps.length - 1 && (
                <span className="absolute top-5 left-[calc(50%+18px)] right-[-50%] h-px bg-gradient-to-r from-emerald-400/70 to-white/20" />
              )}
              <div
                className={`relative z-10 flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-full border ${
                  isLast
                    ? "bg-emerald-500 border-emerald-300 text-[#0F172A] shadow-[0_0_24px_rgba(16,185,129,0.45)]"
                    : "bg-[#0F172A]/70 border-white/20 text-emerald-300"
                }`}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.25} />
              </div>
              <p
                className={`mt-2.5 text-[10px] sm:text-xs font-semibold leading-tight ${
                  isLast ? "text-emerald-300" : "text-slate-200"
                }`}
              >
                {step}
              </p>
            </li>
          );
        })}
      </ol>

      <div className="mt-5 rounded-xl border border-white/10 bg-black/20 px-3 py-2.5 flex items-center justify-between gap-2">
        <span className="text-[11px] text-slate-300">{copy.steps[0]}</span>
        <ArrowRight className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
        <span className="text-[11px] font-semibold text-emerald-300">
          {copy.steps[copy.steps.length - 1]}
        </span>
      </div>
    </div>
  );
}

function FinancialIntelligenceHealthcare() {
  const { lang } = useEmpresasLanguage();
  const t = financialIntelligenceHealthcare[lang] || financialIntelligenceHealthcare.es;

  useEffect(() => {
    const id = "fi-healthcare-fonts";
    if (document.getElementById(id)) return undefined;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Serif:wght@500;600;700&display=swap";
    document.head.appendChild(link);
    return undefined;
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      className="overflow-x-hidden bg-white text-[#0F172A]"
      style={{ fontFamily: '"IBM Plex Sans", system-ui, sans-serif' }}
    >
      {/* BANNER HERO */}
      <section className="relative overflow-hidden text-white">
        <img
          src={sectorSalud}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0F172A]/82" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/88 to-[#0F172A]/45" />
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle at 75% 30%, rgba(16,185,129,0.28), transparent 42%)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-12 lg:py-16">
          <p
            className="text-base sm:text-lg lg:text-xl font-semibold tracking-tight text-emerald-300 max-w-3xl leading-snug"
            style={{ fontFamily: '"IBM Plex Serif", Georgia, serif' }}
          >
            {t.manifesto}
          </p>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-8 lg:gap-10 items-center">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-300">
                {t.eyebrow}
              </p>
              <div className="mt-3 inline-flex flex-col gap-1.5 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3">
                <span className="text-sm font-semibold text-emerald-300">{t.heroAudience}</span>
                <span className="text-xs text-slate-300">{t.heroAudienceMeta}</span>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {t.heroAudienceTags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 text-[11px] font-semibold text-slate-200"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <h1
                className="mt-5 text-3xl sm:text-4xl lg:text-[2.85rem] font-semibold leading-[1.12] tracking-tight"
                style={{ fontFamily: '"IBM Plex Serif", Georgia, serif' }}
              >
                {t.headline}
              </h1>
              <p className="mt-4 text-base sm:text-lg text-slate-200 leading-relaxed max-w-xl">
                {t.subtitle}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={FI_HEALTHCARE_WHATSAPP}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-[#0F172A] hover:bg-emerald-400 transition"
                >
                  {t.ctaPrimary}
                  <ArrowRight className="w-4 h-4" />
                </a>
                <button
                  type="button"
                  onClick={() => scrollTo("metodologia")}
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition"
                >
                  {t.ctaSecondary}
                </button>
              </div>
              <p className="mt-4 text-xs text-slate-400 font-medium tracking-wide">{t.heroNote}</p>
            </div>
            <DecisionVisual copy={t.decisionVisual} />
          </div>
        </div>
      </section>

      {/* PROBLEMA */}
      <section className="py-10 sm:py-12 px-6 sm:px-10 lg:px-16 bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-3xl leading-tight"
            style={{ fontFamily: '"IBM Plex Serif", Georgia, serif' }}
          >
            {t.problemTitle}
          </h2>
          <p className="mt-2 text-lg sm:text-xl text-emerald-300 font-medium">{t.problemSubtitle}</p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            {t.problems.map((problem) => (
              <article
                key={problem.title}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-5"
              >
                <h3 className="text-lg font-semibold">{problem.title}</h3>
                {problem.items ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {problem.items.map((item) => (
                      <span
                        key={item}
                        className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold text-slate-200"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-slate-300 leading-relaxed text-sm">{problem.body}</p>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TESIS */}
      <section className="py-10 sm:py-12 px-6 sm:px-10 lg:px-16">
        <div className="max-w-5xl mx-auto text-center">
          <h2
            className="text-2xl sm:text-3xl font-semibold tracking-tight leading-snug"
            style={{ fontFamily: '"IBM Plex Serif", Georgia, serif' }}
          >
            {t.thesisTitle}
          </h2>
          <div className="mt-6 flex flex-col lg:flex-row items-center justify-center gap-2 lg:gap-1">
            {t.thesisFlow.map((step, index) => (
              <React.Fragment key={step}>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 min-w-[100px] text-sm font-semibold">
                  {step}
                </div>
                {index < t.thesisFlow.length - 1 && (
                  <div className="text-emerald-500">
                    <ArrowDown className="w-4 h-4 lg:hidden" />
                    <ArrowRight className="w-4 h-4 hidden lg:block" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* LO QUE CONSTRUIMOS */}
      <section className="py-10 sm:py-12 px-6 sm:px-10 lg:px-16 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-emerald-600">
            {t.buildEyebrow}
          </p>
          <h2
            className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight"
            style={{ fontFamily: '"IBM Plex Serif", Georgia, serif' }}
          >
            {t.buildTitle}
          </h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {t.capabilities.map((cap, index) => {
              const Icon = CAPABILITY_ICONS[index] || Gauge;
              return (
                <article
                  key={cap.title}
                  className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${
                    index === 4 ? "md:col-span-2 xl:col-span-1" : ""
                  }`}
                >
                  <div className="h-10 w-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold">{cap.title}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed text-sm">{cap.body}</p>
                  <p className="mt-3 pt-3 border-t border-slate-100 text-sm font-semibold text-emerald-700">
                    {cap.result}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* CÓMO TRABAJAMOS */}
      <section className="py-10 sm:py-12 px-6 sm:px-10 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <h2
              className="text-2xl sm:text-3xl font-semibold tracking-tight"
              style={{ fontFamily: '"IBM Plex Serif", Georgia, serif' }}
            >
              {t.howTitle}
            </h2>
            <p className="mt-2 text-slate-500 font-medium leading-relaxed">{t.howSubtitle}</p>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5 lg:p-6">
            <ol className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 lg:gap-0">
              {t.howSteps.map((step, index) => (
                <li key={step.title} className="relative flex xl:block">
                  {index < t.howSteps.length - 1 && (
                    <span className="hidden xl:block absolute top-6 left-[calc(50%+24px)] right-[-8px] h-px bg-slate-200" />
                  )}
                  <div className="relative z-10 flex gap-3 xl:flex-col xl:gap-0 xl:text-center xl:px-2 w-full rounded-xl bg-white border border-slate-200 p-3.5 xl:border-0 xl:bg-transparent xl:p-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0F172A] text-sm font-semibold text-white xl:mx-auto">
                      {index + 1}
                    </div>
                    <div className="xl:mt-3">
                      <p className="text-sm font-semibold text-[#0F172A]">{step.title}</p>
                      <p className="mt-1 text-sm text-slate-500 leading-snug">{step.body}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* QUÉ RECIBE CADA MES */}
      <section className="py-10 sm:py-12 px-6 sm:px-10 lg:px-16 bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-emerald-400">
            {t.monthlyEyebrow}
          </p>
          <h2
            className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight"
            style={{ fontFamily: '"IBM Plex Serif", Georgia, serif' }}
          >
            {t.monthlyTitle}
          </h2>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {t.monthlyItems.map((item) => (
              <li
                key={item}
                className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-sm font-medium flex items-start gap-3"
              >
                <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* RESULTADOS DE NEGOCIO */}
      <section className="py-10 sm:py-12 px-6 sm:px-10 lg:px-16 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2
            className="text-2xl sm:text-3xl font-semibold tracking-tight"
            style={{ fontFamily: '"IBM Plex Serif", Georgia, serif' }}
          >
            {t.resultsTitle}
          </h2>
          <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="grid grid-cols-1 sm:grid-cols-2 bg-[#0F172A] text-white text-xs font-semibold uppercase tracking-[0.18em]">
              <div className="px-5 py-3.5">{t.resultsHeaders[0]}</div>
              <div className="px-5 py-3.5 border-t sm:border-t-0 sm:border-l border-white/10">
                {t.resultsHeaders[1]}
              </div>
            </div>
            {t.results.map((row) => (
              <div
                key={row.capability}
                className="grid grid-cols-1 sm:grid-cols-2 border-t border-slate-100"
              >
                <div className="px-5 py-3.5 font-semibold text-[#0F172A]">{row.capability}</div>
                <div className="px-5 py-3.5 text-slate-600 border-t sm:border-t-0 sm:border-l border-slate-100">
                  {row.result}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EJEMPLOS DE CAPACIDADES */}
      <section className="py-10 sm:py-12 px-6 sm:px-10 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <h2
            className="text-2xl sm:text-3xl font-semibold tracking-tight"
            style={{ fontFamily: '"IBM Plex Serif", Georgia, serif' }}
          >
            {t.examplesTitle}
          </h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
            {t.examples.map((example) => (
              <div
                key={example}
                className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700"
              >
                {example}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* METODOLOGÍA */}
      <section id="metodologia" className="scroll-mt-24 py-10 sm:py-12 px-6 sm:px-10 lg:px-16 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-emerald-600">
            {t.methodSubtitle}
          </p>
          <h2
            className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight max-w-4xl"
            style={{ fontFamily: '"IBM Plex Serif", Georgia, serif' }}
          >
            {t.methodTitle}
          </h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-5 gap-3">
            {t.methodPillars.map((pillar, index) => (
              <article
                key={pillar}
                className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-600">
                  0{index + 1}
                </p>
                <h3 className="mt-2 text-base font-semibold">{pillar}</h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* DIFERENCIA */}
      <section className="py-10 sm:py-12 px-6 sm:px-10 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-2xl sm:text-3xl font-semibold tracking-tight"
            style={{ fontFamily: '"IBM Plex Serif", Georgia, serif' }}
          >
            {t.diffTitle}
          </h2>
          <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="bg-[#0F172A] text-white">
                <tr>
                  {t.diffHeaders.map((header, index) => (
                    <th
                      key={header}
                      className={`px-5 py-3.5 font-semibold ${
                        index === 2 ? "bg-emerald-600" : ""
                      }`}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.diffRows.map((row) => (
                  <tr key={row.join("-")} className="border-t border-slate-100">
                    {row.map((cell, index) => (
                      <td
                        key={`${row[0]}-${cell}`}
                        className={`px-5 py-3.5 ${
                          index === 2
                            ? "bg-emerald-50 font-semibold text-[#0F172A]"
                            : "text-slate-600"
                        }`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CLIENTE IDEAL */}
      <section className="py-10 sm:py-12 px-6 sm:px-10 lg:px-16 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2
            className="text-2xl sm:text-3xl font-semibold tracking-tight"
            style={{ fontFamily: '"IBM Plex Serif", Georgia, serif' }}
          >
            {t.whoTitle}
          </h2>
          <p className="mt-2 text-slate-600 max-w-3xl leading-relaxed">{t.whoSubtitle}</p>

          <article className="mt-6 rounded-2xl border border-emerald-200 bg-white p-6 sm:p-8 shadow-sm ring-1 ring-emerald-100">
            <span className="rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-700">
              {t.idealClient.badge}
            </span>
            <h3
              className="mt-3 text-xl sm:text-2xl font-semibold tracking-tight"
              style={{ fontFamily: '"IBM Plex Serif", Georgia, serif' }}
            >
              {t.idealClient.title}
            </h3>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {t.idealClient.metrics.map((metric) => (
                <div
                  key={metric.label}
                  className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    {metric.label}
                  </p>
                  <p className="mt-1.5 text-base font-semibold text-[#0F172A]">{metric.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                  {t.idealClient.structureTitle}
                </p>
                <ul className="mt-3 space-y-2.5">
                  {t.idealClient.structure.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-slate-600 leading-relaxed text-sm">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                  {t.idealClient.painsTitle}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {t.idealClient.pains.map((pain) => (
                    <span
                      key={pain}
                      className="rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 text-sm font-semibold text-slate-700"
                    >
                      {pain}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </article>

          <p className="mt-8 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">
            {t.whoRolesTitle}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {t.who.map((role) => (
              <span
                key={role}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm"
              >
                {role}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 px-6 sm:px-10 lg:px-16 bg-[#0F172A] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-base text-slate-300">{t.finalLead}</p>
          <h2
            className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight leading-tight"
            style={{ fontFamily: '"IBM Plex Serif", Georgia, serif' }}
          >
            {t.finalTitle}
          </h2>
          <a
            href={FI_HEALTHCARE_WHATSAPP}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 text-sm font-semibold text-[#0F172A] hover:bg-emerald-400 transition"
          >
            {t.finalCta}
            <ArrowRight className="w-4 h-4" />
          </a>
          <div className="mt-6">
            <Link to="/empresas/salud" className="text-sm text-slate-400 hover:text-white transition">
              ← {t.backSalud}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default FinancialIntelligenceHealthcare;
